// In-app support widget.

import { useEffect, useState } from 'react';
import { SUPPORT_EMAIL, buildSupportContext, supportMailto, type SupportContext } from '@/lib/support';
import { useFocusTrap } from './useFocusTrap';

/** Any surface that docks the support entry (the Desk sidebar) opens the widget with this event. */
export const SUPPORT_OPEN_EVENT = 'zoe.support.open';

export interface SupportWidgetProps {
  // Position above the Ask Zoe button + the Pin rail.
  bottomOffset?: number;
  // Extra context folded into the drafted email (route, error id, …).
  context?: SupportContext;
}

export function SupportWidget({ bottomOffset = 168, context }: SupportWidgetProps) {
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState('Help with Zoe');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);

  function buildMailto(): string {
    return supportMailto(subject, `${body || ''}${buildSupportContext(context)}`);
  }

  function onSend() {
    if (typeof window === 'undefined') return;
    window.location.href = buildMailto();
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setOpen(false);
      setBody('');
    }, 2000);
  }

  const dialogRef = useFocusTrap<HTMLElement>(open, () => setOpen(false));

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(SUPPORT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SUPPORT_OPEN_EVENT, onOpen);
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Contact support"
        className="v-support-pill"
        style={{
          position: 'fixed',
          right: 24,
          bottom: bottomOffset,
          padding: '8px 12px',
          minHeight: 36,
          display: 'inline-flex',
          alignItems: 'center',
          background: 'var(--v-paper, #FFFFFF)',
          color: 'var(--v-ink, var(--zk-text))',
          border: '1px solid var(--v-line, var(--zk-border))',
          borderRadius: 18,
          boxShadow: '0 4px 14px rgba(17,19,24,0.08)',
          fontSize: 11,
          fontFamily: 'inherit',
          cursor: 'pointer',
          zIndex: 60,
        }}
      >
        ◐ support
      </button>
    );
  }

  return (
    <aside
      className="v-support-widget"
      ref={dialogRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label="Contact support"
      style={{
        position: 'fixed',
        right: 24,
        bottom: bottomOffset,
        width: 340,
        padding: 16,
        background: 'var(--v-paper, #FFFFFF)',
        border: '1px solid var(--v-line, var(--zk-border))',
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(17,19,24,0.12)',
        zIndex: 75,
        fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ fontSize: 13 }}>Need a hand?</strong>
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close support widget"
          className="btn-reset"
          style={{ cursor: 'pointer', minWidth: 44, minHeight: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, color: 'var(--v-ink-3)' }}
        >
          ×
        </button>
      </div>
      <p className="v-caption" style={{ fontSize: 11, marginBottom: 12 }}>
        Drafts an email to <code>{SUPPORT_EMAIL}</code> with your URL and a context footer prefilled. We reply within 1 business day.
      </p>
      <input
        type="text"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        placeholder="Subject"
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 12,
          marginBottom: 8,
          background: 'var(--v-paper-2, var(--zk-n-100))',
          border: '1px solid var(--v-line, var(--zk-border))',
          borderRadius: 6,
          fontFamily: 'inherit',
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="What's going on?"
        rows={4}
        style={{
          width: '100%',
          padding: '8px 10px',
          fontSize: 12,
          background: 'var(--v-paper-2, var(--zk-n-100))',
          border: '1px solid var(--v-line, var(--zk-border))',
          borderRadius: 6,
          resize: 'vertical',
          fontFamily: 'inherit',
          outline: 'none',
          boxSizing: 'border-box',
          marginBottom: 10,
        }}
      />
      <button
        type="button"
        onClick={onSend}
        disabled={sent}
        style={{
          width: '100%',
          padding: '9px 14px',
          fontSize: 12,
          background: sent ? 'var(--v-teal-action, var(--zk-accent-strong))' : 'var(--v-ink, var(--zk-text))',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          cursor: sent ? 'default' : 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {sent ? '✓ opened email' : 'Open email'}
      </button>
    </aside>
  );
}
